import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Package, Plus, Pencil, Search, Loader2, Barcode
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const ProductsPage = () => {
  const { api } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    cost: '',
    sku: '',
    barcode: '',
    category_id: '',
    track_stock: false,
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [prodsRes, catsRes] = await Promise.all([
        api.get('/tenant/products?active_only=false'),
        api.get('/tenant/categories')
      ]);
      setProducts(prodsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [api]);

  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.barcode && p.barcode.includes(searchQuery));
    const matchesCategory = selectedCategory === 'all' || p.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const openNewDialog = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      price: '',
      cost: '',
      sku: '',
      barcode: '',
      category_id: '',
      track_stock: false,
      description: '',
    });
    setShowDialog(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      cost: (product.cost || 0).toString(),
      sku: product.sku || '',
      barcode: product.barcode || '',
      category_id: product.category_id || '',
      track_stock: product.track_stock,
      description: product.description || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost) || 0,
        category_id: formData.category_id || null,
      };

      if (editingProduct) {
        await api.patch(`/tenant/products/${editingProduct.id}`, data);
        toast.success('Product updated');
      } else {
        await api.post('/tenant/products', data);
        toast.success('Product created');
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Uncategorized';
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.color || '#6b7280';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11"
              data-testid="product-search"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNewDialog} data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredProducts.map(product => (
          <Card 
            key={product.id} 
            className={`cursor-pointer hover:border-primary/50 transition-colors ${!product.is_active ? 'opacity-50' : ''}`}
            onClick={() => openEditDialog(product)}
            data-testid={`product-card-${product.id}`}
          >
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <Badge 
                  variant="secondary"
                  style={{ backgroundColor: `${getCategoryColor(product.category_id)}20`, color: getCategoryColor(product.category_id) }}
                >
                  {getCategoryName(product.category_id)}
                </Badge>
                {!product.is_active && <Badge variant="destructive">Inactive</Badge>}
              </div>
              <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
              <div className="text-2xl font-bold text-primary mb-2">
                {formatCurrency(product.price)}
              </div>
              <div className="flex gap-2 text-sm text-muted-foreground">
                {product.sku && <span>SKU: {product.sku}</span>}
                {product.barcode && (
                  <span className="flex items-center gap-1">
                    <Barcode className="w-3 h-3" />
                    {product.barcode}
                  </span>
                )}
              </div>
              {product.track_stock && (
                <Badge variant="outline" className="mt-2">
                  <Package className="w-3 h-3 mr-1" />
                  Stock Tracked
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No products found</p>
        </div>
      )}

      {/* Product Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'New Product'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Product Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Coca-Cola 330ml"
                  className="mt-1"
                  data-testid="product-name"
                />
              </div>
              
              <div>
                <Label>Price *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="product-price"
                />
              </div>
              
              <div>
                <Label>Cost</Label>
                <Input
                  type="number"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>SKU</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="e.g., BEV-001"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Barcode</Label>
                <Input
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  placeholder="e.g., 4800011111111"
                  className="mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category_id} 
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 flex items-center justify-between p-3 rounded-lg border border-border">
                <div>
                  <Label className="text-base">Track Stock</Label>
                  <p className="text-sm text-muted-foreground">Enable inventory tracking for this product</p>
                </div>
                <Switch
                  checked={formData.track_stock}
                  onCheckedChange={(checked) => setFormData({ ...formData, track_stock: checked })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
